import { useMemo } from "react";
import styles from "./EnergyManagementPage.module.scss";
import AnalyticsBars, { AnalyticsBarRow } from "../../../../common/components/analytics-component/bar-list/AnalyticsBars";
import AnalyticsCard from "../../../../common/components/analytics-component/card/AnalyticsCard";
import AnalyticsHeader from "../../../../common/components/analytics-component/header/AnalyticsHeader";
import AnalyticsMessage from "../../../../common/components/analytics-component/message/AnalyticsMessage";
import AnalyticsStat from "../../../../common/components/analytics-component/stat/AnalyticsStat";
import AnalyticsState from "../../../../common/components/analytics-component/state/AnalyticsState";
import { CardIcon, PaymentFFIcon, PaymentIcon } from "../../../../assets/icons";
import { useGetEnergyChartHealthQuery, useGetFlowFunnelQuery } from "../../../../service/business-analytics-service";
import { AnalyticsPlatformParams } from "../../../../model/bps-admin/business-analytics";

// No insideLabel, so these read as plain thin bars and the share is read off the right.
const CHART_RANGES: AnalyticsBarRow[] = [
	{ label: "Weekly", percent: 58, trailing: "58%" },
	{ label: "Whole month", percent: 44, trailing: "44%" },
	{ label: "Day-on-day", percent: 26, trailing: "26%" },
	{ label: "Yearly", percent: 14, trailing: "14%" },
];

const AUTO_RECHARGE_FLOW_CODE = "auto_recharge_setup";

type Props = {
	/** The page's filters — the section has none of its own. */
	filters: AnalyticsPlatformParams;
};

// How the energy management features are used — consumption, alerts, appliances.
const EnergyManagementSection = ({ filters }: Readonly<Props>) => {
	const {
		data: chartHealth,
		isLoading: isLoadingChartHealth,
		isFetching: isRefetchingChartHealth,
		isError: isChartHealthError,
		refetch: refetchChartHealth,
	} = useGetEnergyChartHealthQuery(filters);

	// Only the last figure is toned: the first two are context, and tinting all three
	// would leave nothing standing out.
	const chartHealthStats = useMemo<React.ComponentProps<typeof AnalyticsStat>[]>(
		() =>
			chartHealth
				? [
						{ label: "Energy mgmt opens", value: chartHealth.opens.toLocaleString(), note: "of DAU this period" },
						{
							label: "Reached a chart",
							value: `${chartHealth.reachedChartPercentage}%`,
							note: `${chartHealth.chartViews.toLocaleString()} views`,
						},
						{
							label: "Chart rendered WITH data",
							value: `${chartHealth.withDataPercentage}%`,
							note: `${chartHealth.emptyPercentage}% open to empty charts`,
							tone: "negative",
						},
				  ]
				: [],
		[chartHealth]
	);

	const {
		data: autoRechargeFunnel,
		isLoading: isLoadingAutoRecharge,
		isFetching: isRefetchingAutoRecharge,
		isError: isAutoRechargeError,
		refetch: refetchAutoRecharge,
	} = useGetFlowFunnelQuery({ code: AUTO_RECHARGE_FLOW_CODE, getErrors: false, ...filters });

	// Sorted here: the response carries an order field but no guarantee it arrives sorted.
	const autoRechargeSteps = useMemo<AnalyticsBarRow[]>(
		() =>
			[...(autoRechargeFunnel?.steps ?? [])]
				.sort((first, second) => first.order - second.order)
				.map(step => ({
					label: step.name,
					percent: step.percentage,
					insideLabel: `${step.percentage}%`,
					trailing: step.reached.toLocaleString(),
				})),
		[autoRechargeFunnel]
	);

	return (
		<>
			{/* Composed here rather than as a component: the pieces are shared, this arrangement is not. */}
			<AnalyticsCard>
				<AnalyticsHeader
					title="Consumption view — health check"
					subtitle="Opened from homepage → did a chart actually render with data?"
				/>
				<AnalyticsState
					loading={isLoadingChartHealth}
					error={isChartHealthError}
					onRetry={refetchChartHealth}
					retrying={isRefetchingChartHealth}
					empty={!chartHealth}
				>
					<div className={styles.statRow}>
						{chartHealthStats.map(stat => (
							<AnalyticsStat key={stat.label} {...stat} />
						))}
					</div>
				</AnalyticsState>
				<AnalyticsMessage tone="negative" title={'This is the "98% failure" — and it\'s a data gap, not a UI bug'}>
					Charts render empty because we store units <em>vended</em> per purchase, not units <em>consumed</em> over time.
					Prepaid meters have no interval readings to plot. Fix = source real consumption data, or relabel these as
					&quot;vending history&quot;.
				</AnalyticsMessage>
			</AnalyticsCard>

			<div className={styles.cardRow}>
				<AnalyticsCard>
					<AnalyticsHeader
						title="Which time range customers open"
						subtitle="Share of energy-management opens · by chart range"
						mock
					/>
					<AnalyticsBars rows={CHART_RANGES} />
					<p className={styles.footnote}>Weekly leads — worth prioritising which range gets real data first.</p>
				</AnalyticsCard>

				<AnalyticsCard>
					<AnalyticsHeader
						title="Auto-recharge setup funnel"
						subtitle={
							autoRechargeFunnel ? `${autoRechargeFunnel.flowsStarted.toLocaleString()} flows` : undefined
						}
					/>
					<AnalyticsState
						loading={isLoadingAutoRecharge}
						error={isAutoRechargeError}
						onRetry={refetchAutoRecharge}
						retrying={isRefetchingAutoRecharge}
						empty={autoRechargeSteps.length === 0}
					>
						<AnalyticsBars rows={autoRechargeSteps} />
					</AnalyticsState>
					<AnalyticsMessage title="The card step loses a third of users" icon={<PaymentFFIcon/>}>
						Saving a card is the wall. Separately, track whether enabled auto-recharges actually <em>execute</em> when
						triggered — setup success ≠ working feature.
					</AnalyticsMessage>
				</AnalyticsCard>
			</div>
		</>
	);
};

export default EnergyManagementSection;
